import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { ConversationList, type ConversationListItem } from "@/components/inbox/ConversationList";
import { ConversationPane } from "@/components/inbox/ConversationPane";
import { ChevronLeft } from "lucide-react";

export function InboxPage() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<string | null>(null);
  const [takeoverFilter, setTakeoverFilter] = useState<boolean | null>(null);

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ["conversations", channelFilter, takeoverFilter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "100" });
      if (channelFilter) params.set("channel", channelFilter);
      if (takeoverFilter !== null) params.set("takeover", String(takeoverFilter));
      return api.get<ConversationListItem[]>(`/api/conversations?${params.toString()}`);
    },
    refetchInterval: 10_000,
  });

  return (
    <div className="flex h-full -m-4 sm:-m-6">
      {/* Conversation list - visible on md+ always, on mobile only when no
          conversation is selected. */}
      <div
        className={
          "w-full md:w-80 shrink-0 border-r border-slate-200 bg-brand-surface overflow-y-auto " +
          (selectedId ? "hidden md:block" : "block")
        }
      >
        <ConversationList
          conversations={conversations}
          isLoading={isLoading}
          selectedId={selectedId}
          onSelect={setSelectedId}
          channelFilter={channelFilter}
          onChannelFilter={setChannelFilter}
          takeoverFilter={takeoverFilter}
          onTakeoverFilter={setTakeoverFilter}
        />
      </div>

      {/* Conversation pane - visible on md+ always, on mobile only when a
          conversation IS selected. */}
      <div
        className={
          "flex-1 overflow-hidden " +
          (selectedId ? "block" : "hidden md:block")
        }
      >
        {selectedId ? (
          <div className="flex h-full flex-col">
            {/* Mobile-only back bar */}
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="md:hidden flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border-b border-slate-200 bg-brand-surface hover:bg-slate-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to inbox
            </button>
            <div className="flex-1 overflow-hidden">
              <ConversationPane
                conversationId={selectedId}
                onRefreshList={() => qc.invalidateQueries({ queryKey: ["conversations"] })}
              />
            </div>
          </div>
        ) : (
          <div className="hidden md:flex h-full items-center justify-center text-sm text-slate-500">
            Select a conversation on the left.
          </div>
        )}
      </div>
    </div>
  );
}
